import {GridPage} from "../../shared/GridPage";
import React, {useRef, useState} from "react";
import {ColumnConfig, GridConfig} from "../../shared/Grid";
import {PagingInfo, SortState} from "../../../../utils/Grid";
import {Link} from "react-router-dom";
import {DateTime} from "luxon";
import {HoldFlag, HoldFlagFetchSearchCriteria} from "../HoldsFlag";
import {useGetHoldFlagReasonsQuery, useGetHoldFlagStatusesQuery, useGetSitesQuery,} from "../../../../app/apiSlice";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {
	canFetchMoreHoldFlags, changeSearch,
	fetchHoldFlags,
	fetchMoreHoldFlags,
	HOLD_FLAGS_CRITERIA_STORAGE_KEY,
	isFetchingHoldFlags,
	refetchAllHoldFlags,
	resetHoldFlagSearchCriteria,
	selectHoldFlagReasons,
	selectHoldFlagReasonsLoading,
	selectHoldFlags,
	selectHoldFlagSearchCriteria,
	selectHoldFlagStatuses,
	selectHoldFlagStatusesLoading,
	setHoldFlagSearchCriteria
} from "../HoldFlagSlice";
import {Consumer} from "../../customer/Consumer";
import {HoldEdit} from "../edit/HoldEdit";
import {CurrencyFormatter} from "../../../../utils/CurrencyFormatter";
import {reportValToOrderLink} from "../../../reports/ReportView";
import {usePersistedSelector} from "../../../../persistence";
import {Button, Spinner} from "reactstrap";

export const HoldsQueueBrowse = () => {
	// Hooks with no parameters
	useGetHoldFlagReasonsQuery();
	useGetHoldFlagStatusesQuery();
	const dispatch = useAppDispatch();
	const {data: sites, isLoading: sitesLoading} = useGetSitesQuery();

	// Selectors
	const holdsFlagReasons = useAppSelector(selectHoldFlagReasons);
	const reasonsLoading = useAppSelector(selectHoldFlagReasonsLoading);
	const holdsFlagStatuses = useAppSelector(selectHoldFlagStatuses);
	const statusesLoading = useAppSelector(selectHoldFlagStatusesLoading);
	const holdFlags = useAppSelector(selectHoldFlags);
	const moreAvailable = useAppSelector(canFetchMoreHoldFlags);
	const isLoading = useAppSelector(isFetchingHoldFlags);
	const fetchStatus = useAppSelector(state => state.holdFlags.status);
	const criteria = usePersistedSelector(
		HOLD_FLAGS_CRITERIA_STORAGE_KEY,
		selectHoldFlagSearchCriteria
	);

	// States
	const [focusedHoldFlag, setFocusedHoldFlag] = useState<HoldFlag | undefined>(undefined);
	const [editHoldFlagIsOpen, setEditHoldFlagIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<SortState<HoldFlag>>({
		column: 'timeOfFlag',
		serverColumn: 'timeOfFlag',
		direction: 'desc'
	});

	// Use a ref here because updating does not need to re-render
	const nextOffsetRef = useRef(0);
	
	// Helper functions
	const onRowClick = (holdFlag: HoldFlag) => {
		setFocusedHoldFlag(holdFlag);
		setEditHoldFlagIsOpen(true);
	};

	const fetchMore = (paging: PagingInfo) => {
		if (fetchStatus !== 'succeeded') return; // don't try to grab again if we're already loading
		dispatch(fetchMoreHoldFlags({
			criteria: {
				...criteria,
				search
			},
			paging: {
				...paging,
				offset: nextOffsetRef.current
			}
		}));
	};

	const closeModal = (shouldRefresh: boolean | undefined = false) => {
		setFocusedHoldFlag(undefined);
		setEditHoldFlagIsOpen(false);
		if (shouldRefresh)
			dispatch(refetchAllHoldFlags({criteria}));
	};

	const onCriteriaChange = (newCriteria: HoldFlagFetchSearchCriteria) => {
		dispatch(setHoldFlagSearchCriteria(newCriteria));
		nextOffsetRef.current = 0;
		dispatch(fetchHoldFlags({
			criteria: {...newCriteria, search},
			paging: {
				sortKey: sort.serverColumn!,
				sortDirection: sort.direction,
				offset: 0
			}
		}));
	}

	const onSort = (newSort: SortState<HoldFlag>) => {
		setSort(newSort);
		nextOffsetRef.current = 0;
		dispatch(fetchHoldFlags({
			criteria: {...criteria, search},
			paging: {
				sortKey: newSort.serverColumn!,
				sortDirection: newSort.direction,
				offset: 0
			}
		}));
	}

	// Other variables
	const isLoadingAnything = isLoading || sitesLoading || statusesLoading || reasonsLoading;
	const config: GridConfig<HoldFlag> = {
		configSections: [
			{
				columns: [
					new ColumnConfig({
						field: 'storefront',
						display: 'Storefront',
						serverColumn: 's.Name',
						isSearchCriteria: false,
						isSortable: false,
						isHidden: false,
					}),
					new ColumnConfig({
						field: 'siteId',
						display: 'Storefront',
						isHidden: true,
						isSortable: false,
						type: 'select',
						choices: [{value: -1, display: 'All Storefronts'}, ...(sites?.map(s => ({
							value: s.id,
							display: s.name
						})) ?? [])],
					}),
					new ColumnConfig({
						field: 'consumer',
						display: 'Consumer',
						isSearchCriteria: false,
						isSortable: false,
						cellDisplay: val => (
							<Link to={`/admin/consumer/${(val as Consumer)?.id}`}>{(val as Consumer)?.email}</Link>),
					}),
					new ColumnConfig({
						field: 'consumerName',
						display: 'Consumer Name',
						serverColumn: 'consumerName',
						isSearchCriteria: false,
						isSortable: false,
					}),
					new ColumnConfig({
						field: 'email',
						display: 'Email',
						isHidden: true,
						isSortable: false,
					}),
					new ColumnConfig({
						field: 'orderId',
						display: 'Order Id',
						isSortable: false,
						isSearchCriteria: false,
						cellDisplay: val => reportValToOrderLink(val as number),
					}),
					new ColumnConfig({
						field: 'aba',
						display: 'ABA',
						isSortable: false,
					}),
					new ColumnConfig({
						field: 'accountNumber',
						display: 'Account Number',
						isSearchCriteria: false,
						isSortable: false,
					}),
					new ColumnConfig<HoldFlag>({
						field: 'orderStatus',
						display: 'Order Status',
						isSearchCriteria: false,
						isSortable: false,
					}),
					new ColumnConfig<HoldFlag>({
						field: 'orderTotal',
						display: 'Order Total',
						isSortable: !isLoadingAnything,
						isSearchCriteria: false,
						cellDisplay: val => val ? `${CurrencyFormatter.format(val as number)}` : "$-.--",
					}),
					new ColumnConfig({
						field: 'holdFlagStatus.name',
						display: 'Status',
						serverColumn: 'HoldStatus',
						isSearchCriteria: false,
						isSortable: false,
					}),
					new ColumnConfig({
						field: 'holdFlagReason.name',
						display: 'Reason',
						serverColumn: 'HoldReason',
						isSearchCriteria: false,
						isSortable: false,
					}),
					new ColumnConfig({
						field: 'timeOfFlag',
						display: 'Time of Flag',
						isSearchCriteria: false,
						isSortable: !isLoadingAnything,
						cellDisplay: val => val ? DateTime.fromISO(`${val as string}Z`).setZone("local").toLocaleString(DateTime.DATETIME_SHORT) : 'Never modified.',
					}),
					new ColumnConfig({
						field: 'daysOnHold',
						serverColumn: 'DaysOnHold',
						display: 'Days on Hold',
						isSearchCriteria: false,
						isSortable: !isLoadingAnything,
						cellDisplay: val => {
							return `${val} days ago`;
						},
					}),
					new ColumnConfig({
						field: 'holdDescription',
						display: 'Description',
						isSortable: false,
						isSearchCriteria: false
					}),
					new ColumnConfig({
						field: 'holdFlagStatusIds',
						display: 'Hold Flag Status',
						isHidden: true,
						isSortable: false,
						type: 'multi-select',
						choices: holdsFlagStatuses?.map(hfs => ({value: hfs.id, display: hfs.name})) ?? []
					}),
					new ColumnConfig({
						field: 'holdFlagReasonIds',
						display: 'Hold Flag Reason',
						isSortable: false,
						isHidden: true,
						type: 'multi-select',
						choices: holdsFlagReasons?.map(hfr => ({value: hfr.id, display: hfr.name})) ?? []
					}),
				]
			}
		]
	};

	function onResetCriteria() {
		dispatch(resetHoldFlagSearchCriteria())
	}

	function handleShouldFetchMore({offset}: PagingInfo) {
		nextOffsetRef.current = offset ?? 0;
	}

	function handleMoreClick() {
		fetchMore({
			sortKey: sort.serverColumn!,
			sortDirection: sort.direction,
			offset: nextOffsetRef.current,
		})
	}

	function handleRefreshClick() {
		dispatch(changeSearch());
		nextOffsetRef.current = 0;
		dispatch(fetchHoldFlags({
			criteria: {...criteria, search},
			paging: {
				sortKey: sort.serverColumn!,
				sortDirection: sort.direction,
				offset: 0
			}
		}));
	}

	return (
		<>
			<GridPage records={holdFlags ?? []}
			          gridConfig={config}
			          defaultCriteria={criteria}
			          title={'Holds'}
			          sort={sort}
			          shouldShowCriteriaPanel={true}
			          onSortChanged={onSort}
			          onSearchChange={setSearch}
			          moreRecordsAvailable={moreAvailable}
			          onCriteriaChange={onCriteriaChange}
			          onShouldFetchMore={handleShouldFetchMore}
			          onRowClick={onRowClick}
			          isLoading={isLoadingAnything}
			          onResetCriteria={onResetCriteria}
					  noRecordsMessage = "No records to display. Enter criteria and click Search."
					  disableSearch={isLoadingAnything}
					  renderGridFooter={() => (
						  <HoldsQueueBrowseFooter hasMore={moreAvailable}
												  colSpan={config.configSections[0].columns.length}
												  onMoreClick={handleMoreClick}
												  isLoading={isLoading}
												  onRefreshClick={handleRefreshClick}
						  />
					  )}
			/>

			{focusedHoldFlag &&
                <HoldEdit
                    requestedHoldFlag={focusedHoldFlag}
                    isOpen={editHoldFlagIsOpen}
                    toggleModal={closeModal}/>
			}

		</>
	)
};

type HoldsQueueBrowseFooterProps = {
	readonly hasMore: boolean;
	readonly isLoading: boolean;
	readonly colSpan: number;
	readonly onMoreClick: () => void;
	readonly onRefreshClick: () => void;
}

const HoldsQueueBrowseFooter = ({
									hasMore,
									isLoading,
									colSpan,
									onMoreClick,
									onRefreshClick,
								}: HoldsQueueBrowseFooterProps) => (
	<tr>
		<td colSpan={colSpan}>
			{hasMore
				? 
				<Button onClick={onMoreClick}>
					Load More
					{isLoading && <Spinner/>}
				</Button>
				: 
				<p>
					No more holds! Please 
					<a onClick={onRefreshClick} className="px-1">
						refresh
					</a>
					to see more.
				</p>
			}
		</td>
	</tr> 
)